import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { eq } from 'tio-ui/utilities';
import { DrawerBody } from 'tio-ui/components/drawer';
import Chip from 'tio-ui/components/chip';
import { formatTransactionAmount } from 'tio-common/utils/course-cash/format-transaction';
import type PcsCardModel from 'tio-data/models/pcs-card';
import type PcsAuthorizationModel from 'tio-data/models/pcs-authorization';
import type PcsCardEventModel from 'tio-data/models/pcs-card-event';
import type PcsTransactionModel from 'tio-data/models/pcs-transaction';
import type IntlService from 'ember-intl/services/intl';

interface ActivityEntry {
  id: string;
  date: string;
  merchantName: string;
  referenceNumber: string;
  amount: number;
  status: 'approved' | 'declined' | 'captured' | 'refunded' | 'reversed' | 'created';
  declineReason: string;
  isLast: boolean;
}

interface ActivityViewSignature {
  Args: {
    card: PcsCardModel;
    cardNumber?: number;
    totalCards?: number;
  };
}

/**
 * CourseCa$h Card Activity View
 *
 * Shows card status and timeline of authorization history.
 * Used inside the Course Cash drawer on the employee TAS show page.
 *
 * @see Figma node 1939:42031
 */
export default class ActivityView extends Component<ActivityViewSignature> {
  @service declare intl: IntlService;

  /**
   * Map the card's PCS state to a human-readable status label.
   */
  get cardStatus() {
    const state = this.args.card.state;
    if (state === 'PCS.CardState.ACTIVATED') return 'active';
    if (state === 'PCS.CardState.INACTIVATED') return 'inactive';
    if (state === 'PCS.CardState.CANCELED') return 'canceled';
    if (state === 'PCS.CardState.CREATED') return 'created';
    return 'unknown';
  }

  private get resolvedCardNumber() {
    return this.args.cardNumber ?? 1;
  }

  private get resolvedTotalCards() {
    return this.args.totalCards ?? 1;
  }

  get isOneOfMultiple() {
    return this.resolvedTotalCards > 1;
  }

  get cardStatusIntent() {
    if (this.cardStatus === 'active') return 'success';
    if (this.cardStatus === 'inactive' || this.cardStatus === 'canceled') return 'danger';
    if (this.cardStatus === 'created') return 'warning';
    return 'default';
  }

  get cardStatusLabel() {
    const status = this.cardStatus;
    const keys: Record<string, string> = {
      active: 'tuition_assistance.course_cash.activity.card_status_active',
      inactive: 'tuition_assistance.course_cash.activity.card_status_inactive',
      canceled: 'tuition_assistance.course_cash.activity.card_status_canceled',
      created: 'tuition_assistance.course_cash.activity.card_status_created',
      unknown: 'tuition_assistance.course_cash.activity.card_status_unknown',
    };
    const key = keys[status] ?? 'tuition_assistance.course_cash.activity.card_status_unknown';
    return this.intl.t(key);
  }

  /**
   * Build a sorted timeline of authorization events + card creation.
   * Sorted newest-first (descending by date). Each entry includes an `isLast`
   * flag so the template can hide the connecting line on the final entry.
   *
   * Each authorization is expanded into per-transition entries using its
   * transitionLog so that APPROVED ("Authorized") and CAPTURED appear as
   * separate rows. Falls back to a single entry when no transitionLog is
   * available.
   */
  get activityTimeline(): ActivityEntry[] {
    const card = this.args.card;
    const authorizations = card.pcsAuthorizations ?? [];

    const entries: ActivityEntry[] = [];

    for (const auth of authorizations as PcsAuthorizationModel[]) {
      const providerData = auth.providerData as Record<string, unknown> | undefined;
      const merchantData = providerData?.merchant_data as Record<string, string> | undefined;
      const merchantName =
        merchantData?.name ??
        this.intl.t('tuition_assistance.course_cash.activity.unknown_merchant');
      const networkId = merchantData?.network_id ?? auth.providerId ?? '';

      // Resolve the base date for fallback. providerData.created originates from
      // Python's bounce_unix_epoch_isoformat (UNIX epoch → naive ISO string).
      // The value is actually UTC but lacks a timezone indicator, so we must
      // normalize it before the browser interprets it as local time.
      const providerCreated = providerData?.created;
      const baseDate =
        typeof providerCreated === 'string' && providerCreated.length > 0
          ? this.normalizeToUtc(providerCreated)
          : (auth.createdAt ?? new Date(0).toISOString());

      // Expand into per-transition entries when a transitionLog is present.
      // We emit rows for APPROVED ("Authorized"), CAPTURED, DECLINED, and
      // REVERSED transitions — REQUESTED is an internal queueing state, not
      // meaningful to the employee.
      const transitionLog = auth.transitionLog ?? [];
      const relevantTransitions = transitionLog.filter((log) => {
        const to = log.to ?? '';
        return (
          to.includes('APPROVED') ||
          to.includes('CAPTURED') ||
          to.includes('DECLINED') ||
          to.includes('REVERSED')
        );
      });

      if (relevantTransitions.length > 0) {
        for (const logEntry of relevantTransitions) {
          const toState = logEntry.to ?? '';
          let status: ActivityEntry['status'];

          if (toState.includes('CAPTURED')) {
            status = 'captured';
          } else if (toState.includes('DECLINED')) {
            status = 'declined';
          } else if (toState.includes('REVERSED')) {
            status = 'reversed';
          } else {
            // APPROVED — shown as "Authorized"
            status = 'approved';
          }

          let declineReason = '';
          if (status === 'declined') {
            // Prefer the reason stored directly in this log entry
            const logDeclineReasons = logEntry.reason?.['decline_reasons'] as
              | Record<string, Record<string, unknown>>
              | undefined;
            declineReason = logDeclineReasons
              ? this.formatPcsDeclineReasons(logDeclineReasons)
              : this.extractDeclineReason(auth);
          }

          // For reversed authorizations Stripe zeros the amount field.
          // Recover the original amount from request_history.
          let entryAmount = auth.amount ?? 0;
          if (status === 'reversed' && entryAmount === 0) {
            const requestHistory = (providerData?.request_history ?? []) as Array<{
              amount?: number;
            }>;
            const firstRequest = requestHistory[0];
            if (firstRequest?.amount != null) {
              entryAmount = firstRequest.amount;
            }
          }

          entries.push({
            id: `${auth.id ?? crypto.randomUUID()}-${toState}`,
            date: logEntry.timestamp || baseDate,
            merchantName,
            referenceNumber: networkId,
            amount: entryAmount,
            status,
            declineReason,
            isLast: false,
          });
        }
      } else {
        // Fallback: single entry using the authorization's final state.
        // Skip REQUESTED-only authorizations — REQUESTED is an internal
        // queueing state and should not appear in the employee timeline.
        const authState = auth.state ?? '';
        if (
          authState.includes('REQUESTED') &&
          !authState.includes('APPROVED') &&
          !authState.includes('CAPTURED') &&
          !authState.includes('DECLINED')
        ) {
          continue;
        }
        const status = this.mapAuthorizationStatus(authState);
        let declineReason = '';
        if (status === 'declined') {
          declineReason = this.extractDeclineReason(auth);
        }

        let fallbackAmount = auth.amount ?? 0;
        if (status === 'reversed' && fallbackAmount === 0) {
          const requestHistory = (providerData?.request_history ?? []) as Array<{
            amount?: number;
          }>;
          const firstRequest = requestHistory[0];
          if (firstRequest?.amount != null) {
            fallbackAmount = firstRequest.amount;
          }
        }

        entries.push({
          id: auth.id ?? crypto.randomUUID(),
          date: baseDate,
          merchantName,
          referenceNumber: networkId,
          amount: fallbackAmount,
          status,
          declineReason,
          isLast: false,
        });
      }
    }

    // Add refund entries from pcs-transactions (state includes "REFUNDED")
    const transactions = card.pcsTransactions ?? [];
    for (const txn of transactions as PcsTransactionModel[]) {
      const txnState = txn.state ?? '';
      if (!txnState.includes('REFUNDED')) continue;

      const txnProviderData = txn.providerData as Record<string, unknown> | undefined;
      const txnMerchantData = txnProviderData?.merchant_data as Record<string, string> | undefined;
      const txnCreated = txnProviderData?.created;
      let txnDate = '';
      if (typeof txnCreated === 'string' && txnCreated.length > 0) {
        txnDate = this.normalizeToUtc(txnCreated);
      } else {
        txnDate = txn.createdAt ?? '';
      }

      entries.push({
        id: txn.id ?? crypto.randomUUID(),
        date: txnDate,
        merchantName:
          txnMerchantData?.name ??
          this.intl.t('tuition_assistance.course_cash.activity.unknown_merchant'),
        referenceNumber: txnMerchantData?.network_id ?? txn.providerId ?? '',
        amount: txn.amount ?? 0,
        status: 'refunded',
        declineReason: '',
        isLast: false,
      });
    }

    // Add a "Card Created" entry. Prefer the CARD_ISSUED event's createdAt
    // (a Rails-generated ISO 8601 timestamp with +00:00 offset) for consistent
    // timezone handling. Fall back to providerData.created (from Python's
    // bounce_unix_epoch_isoformat — naive ISO, actually UTC) with normalizeToUtc.
    const cardEvents = card.pcsCardEvents ?? [];
    const issuedEvent = (cardEvents as PcsCardEventModel[]).find(
      (evt) => evt.event === 'PCS.CardEvent.CARD_ISSUED'
    );
    const cardCreatedDate =
      issuedEvent?.createdAt ?? this.normalizeToUtc(String(card.providerData?.created ?? ''));
    if (cardCreatedDate) {
      entries.push({
        id: 'card-created',
        date: cardCreatedDate,
        merchantName: '',
        referenceNumber: '',
        amount: 0,
        status: 'created',
        declineReason: '',
        isLast: false,
      });
    }

    // Sort newest first
    const sorted = entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Mark the last entry
    if (sorted.length > 0) {
      sorted[sorted.length - 1]!.isLast = true;
    }

    return sorted;
  }

  /**
   * Extract the decline reason from either the PCS transition_log (which has
   * rich contextual reasons like "Amount exceeds available balance") or fall
   * back to Stripe's generic request_history reason code.
   */
  private extractDeclineReason = (auth: PcsAuthorizationModel): string => {
    // 1. Try PCS transition_log — contains structured decline_reasons
    const transitionLog = auth.transitionLog;
    if (Array.isArray(transitionLog)) {
      for (const entry of transitionLog) {
        const declineReasons = entry.reason?.decline_reasons as
          | Record<string, Record<string, unknown>>
          | undefined;
        if (declineReasons) {
          return this.formatPcsDeclineReasons(declineReasons);
        }
      }
    }

    // 2. Fallback to Stripe request_history
    const providerData = auth.providerData as Record<string, unknown> | undefined;
    const requestHistory = providerData?.request_history as Array<{ reason?: string }> | undefined;
    const lastEntry = requestHistory?.[requestHistory.length - 1];
    if (lastEntry?.reason && lastEntry.reason !== 'webhook_declined') {
      return this.formatStripeReason(lastEntry.reason);
    }

    return '';
  };

  /**
   * Format PCS-specific decline reason codes into user-friendly descriptions.
   * These come from the tio-cards authorization decision logic and contain
   * rich context about exactly why a transaction was declined.
   */
  private formatPcsDeclineReasons = (reasons: Record<string, Record<string, unknown>>): string => {
    const descriptions: string[] = [];

    for (const [code, detail] of Object.entries(reasons)) {
      switch (code) {
        case 'PCS.Decline.Amount.Excessive': {
          const available = (detail.card as Record<string, unknown> | undefined)?.utilization as
            | Record<string, number>
            | undefined;
          if (available?.available != null) {
            descriptions.push(
              this.intl.t('tuition_assistance.course_cash.activity.decline_insufficient_balance', {
                amount: this.formatAmount(available.available),
              })
            );
          } else {
            descriptions.push(
              this.intl.t(
                'tuition_assistance.course_cash.activity.decline_insufficient_balance_generic'
              )
            );
          }
          break;
        }
        case 'PCS.Decline.Merchant.CategoryCode.Invalid': {
          const mcc = detail.mcc as string | undefined;
          descriptions.push(
            mcc
              ? this.intl.t('tuition_assistance.course_cash.activity.decline_merchant_category', {
                  mcc,
                })
              : this.intl.t(
                  'tuition_assistance.course_cash.activity.decline_merchant_category_generic'
                )
          );
          break;
        }
        case 'PCS.Decline.Card.NotActive':
          descriptions.push(
            this.intl.t('tuition_assistance.course_cash.activity.decline_card_inactive')
          );
          break;
        case 'PCS.Decline.External': {
          const note = detail.note as string | undefined;
          const reason = detail.reason as string | undefined;
          const label = reason ? this.formatStripeReason(reason) : null;
          const message =
            note ?? this.intl.t('tuition_assistance.course_cash.activity.decline_network');
          descriptions.push(label ? `${label}: ${message}` : message);
          break;
        }
        default:
          // Unknown PCS code — format as readable label
          descriptions.push(code.replace(/^PCS\.Decline\./, '').replace(/\./g, ' — '));
      }
    }

    return descriptions.join('; ');
  };

  /**
   * Format a Stripe decline reason code to a readable label (fallback only).
   */
  private formatStripeReason = (reason: string): string => {
    const keys: Record<string, string> = {
      spending_controls: 'tuition_assistance.course_cash.activity.stripe_spending_controls',
      insufficient_funds: 'tuition_assistance.course_cash.activity.stripe_insufficient_funds',
      card_inactive: 'tuition_assistance.course_cash.activity.stripe_card_inactive',
      suspected_fraud: 'tuition_assistance.course_cash.activity.stripe_suspected_fraud',
      not_allowed: 'tuition_assistance.course_cash.activity.stripe_not_allowed',
    };
    const key = keys[reason];
    return key ? this.intl.t(key) : reason.replace(/_/g, ' ');
  };

  /**
   * Map PCS authorization state enum to a simplified status for the UI.
   */
  private mapAuthorizationStatus = (state: string): ActivityEntry['status'] => {
    if (state.includes('DECLINED')) return 'declined';
    if (state.includes('CAPTURED')) return 'captured';
    if (state.includes('REVERSED')) return 'reversed';
    if (state.includes('APPROVED')) return 'approved';
    // REQUESTED is filtered out before reaching this fallback; treat any
    // remaining unknown state as approved rather than silently hiding it.
    return 'approved';
  };

  /**
   * Normalize a date string that originated from a UNIX epoch conversion in
   * Python (bounce_unix_epoch_isoformat). These strings are valid ISO 8601 but
   * lack a timezone offset — the values are actually UTC. Without 'Z', browsers
   * parse them as local time (per ECMA-262), causing sort/display mismatches
   * when mixed with Rails timestamps that include '+00:00'.
   */
  private normalizeToUtc = (dateString: string) => {
    if (!dateString) return dateString;
    // Already has timezone info — leave as-is
    if (/[Zz]$/.test(dateString) || /[+-]\d{2}:\d{2}$/.test(dateString)) {
      return dateString;
    }
    return dateString + 'Z';
  };

  /**
   * Format a date string for the timeline display.
   * Example: "02/01/2026 • 1:45 AM"
   * Returns a fallback string if the date is invalid to prevent render crashes.
   */
  formatTimelineDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const datePart = this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    const timePart = this.intl.formatDate(date, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart} \u2022 ${timePart}`;
  };

  /**
   * Format an amount in cents to a currency string.
   */
  formatAmount = (amountInCents: number) => {
    return formatTransactionAmount(amountInCents, this.intl);
  };

  /**
   * Returns the semantic background class for the timeline dot, mapping each
   * status to its intent shade (1:1 with the row's `<Chip @intent>`):
   * declined→danger, approved→success, captured→info, refunded→warning,
   * reversed→brand, otherwise neutral.
   */
  dotClass = (status: string) => {
    if (status === 'declined') return 'tio-bg-danger';
    if (status === 'approved') return 'tio-bg-success';
    if (status === 'captured') return 'tio-bg-info';
    if (status === 'refunded') return 'tio-bg-warning';
    if (status === 'reversed') return 'tio-bg-brand';
    return 'tio-bg-neutral';
  };

  <template>
    <div class="tio-col-flush">
      {{! Multi-card section header — only shown when there are multiple cards }}
      {{#if this.isOneOfMultiple}}
        <div
          class="tio-row-between px-6 py-2.5
            {{if (eq this.resolvedCardNumber 1) '' 'border-t tio-border-default'}}
            tio-bg-sunken"
        >
          <span class="tio-text-xs-semibold tio-fg-secondary uppercase tracking-wide">
            {{t
              "tuition_assistance.course_cash.activity.card_n_of_total"
              number=this.resolvedCardNumber
              total=this.resolvedTotalCards
            }}
          </span>
          {{#if @card.providerData.last4}}
            <span class="text-xs tio-fg-muted">
              ••••
              {{@card.providerData.last4}}
            </span>
          {{/if}}
        </div>
      {{/if}}
      <DrawerBody>
        {{! Status and Actions bar }}
        <div data-test-activity-status-bar class="tio-row-between py-3 mb-4">
          <div class="tio-row-tight">
            <span class="tio-text-sm-medium tio-fg-secondary">
              {{t "tuition_assistance.course_cash.activity.status_label"}}
            </span>
            <Chip @intent={{this.cardStatusIntent}} @size="sm" @appearance="faded">
              {{this.cardStatusLabel}}
            </Chip>
          </div>
          {{#if (eq this.isOneOfMultiple false)}}
            {{#if @card.providerData.last4}}
              <span class="text-xs tio-fg-muted">
                ••••
                {{@card.providerData.last4}}
              </span>
            {{/if}}
          {{/if}}
        </div>

        {{! History Timeline }}
        <div data-test-activity-timeline class="tio-col-flush">
          {{#each this.activityTimeline as |entry|}}
            <div class="flex" data-test-timeline-entry>
              {{! Timeline indicator column }}
              <div class="tio-stack-center mr-4 w-3">
                <div
                  class="w-3 h-3 rounded-full mt-1 shrink-0 {{this.dotClass entry.status}}"
                ></div>
                {{#unless entry.isLast}}
                  <div class="w-0.5 flex-1 tio-bg-sunken min-h-10"></div>
                {{/unless}}
              </div>

              {{! Entry content }}
              <div class="flex-1 pb-6">
                {{#if (eq entry.status "created")}}
                  <div class="text-xs tio-fg-muted">
                    {{this.formatTimelineDate entry.date}}
                  </div>
                  <div class="tio-text-sm-medium tio-fg-secondary mt-0.5">
                    {{t "tuition_assistance.course_cash.activity.card_created"}}
                  </div>
                {{else}}
                  {{! Row 1: Date · Amount }}
                  <div class="tio-row-between">
                    <span class="text-xs tio-fg-muted">
                      {{this.formatTimelineDate entry.date}}
                    </span>
                    <span class="tio-text-sm-semibold tio-fg-primary">
                      {{this.formatAmount entry.amount}}
                    </span>
                  </div>

                  {{! Row 2: Merchant · Status badge }}
                  <div class="tio-row-between mt-1">
                    <div class="tio-fill-min mr-3">
                      <div class="tio-text-sm-medium tio-fg-primary truncate">
                        {{entry.merchantName}}
                      </div>
                    </div>
                    {{#if (eq entry.status "declined")}}
                      <Chip @intent="danger" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.declined"}}
                      </Chip>
                    {{else if (eq entry.status "approved")}}
                      <Chip @intent="success" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.approved"}}
                      </Chip>
                    {{else if (eq entry.status "captured")}}
                      <Chip @intent="primary" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.captured"}}
                      </Chip>
                    {{else if (eq entry.status "refunded")}}
                      <Chip @intent="warning" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.refunded"}}
                      </Chip>
                    {{else if (eq entry.status "reversed")}}
                      <Chip @intent="default" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.reversed"}}
                      </Chip>
                    {{/if}}
                  </div>

                  {{! Row 3: Decline reason (only for declined) }}
                  {{#if entry.declineReason}}
                    <div class="mt-1.5 text-xs tio-fg-danger-body leading-snug text-pretty">
                      {{entry.declineReason}}
                    </div>
                  {{/if}}
                {{/if}}
              </div>
            </div>
          {{else}}
            <p class="text-sm tio-fg-muted italic py-4">
              {{t "tuition_assistance.course_cash.activity.no_activity"}}
            </p>
          {{/each}}
        </div>
      </DrawerBody>
    </div>
  </template>
}
